/**
 * What the user hands to the upload flow, and how it is named.
 *
 * This module owns the *file* axis alone: which extensions Virtuosa accepts,
 * which family a picked file belongs to, and how its name reads once the
 * extension is gone. It deliberately says nothing about the pipeline the file
 * then travels through — that is `Upload["mode"]`, in
 * `components/ui/molecules/file-drop-zone.tsx`.
 *
 * Kept UI-free so the drop surfaces (modal drop zone, window-wide drop
 * catcher) share one answer instead of each carrying its own extension list,
 * and so the classification can be unit-tested without a DOM.
 */

/**
 * A file that already encodes the notes themselves, and can go straight to the
 * analyzer.
 *
 * - `midi`     : a performance. Nothing says which hand plays what; the
 *                analyzer infers it.
 * - `musicxml` : an engraved score. Staves, and therefore hands, are written
 *                in the file; repeats and jumps are written too.
 */
export type ScoreFormat = "midi" | "musicxml";

/**
 * Every family the upload flow accepts, as the UI needs to name it. `image`
 * covers photos and PDFs alike: both are pictures of a score that must be read
 * before anything else can happen.
 */
export type UploadFileKind = ScoreFormat | "image";

const MIDI_EXTENSIONS = [".mid", ".midi"] as const;
const MIDI_MIME_TYPES: readonly string[] = ["audio/midi", "audio/x-midi"];

/**
 * `.mxl` is the zipped container, `.xml` the extension a lot of engravers
 * still default to. Accepting `.xml` means a non-MusicXML XML reaches the
 * analyzer, which answers for it — the front cannot tell from the name alone.
 */
const MUSICXML_EXTENSIONS = [".musicxml", ".mxl", ".xml"] as const;
const MUSICXML_MIME_TYPES: readonly string[] = [
  "application/vnd.recordare.musicxml+xml",
  "application/vnd.recordare.musicxml",
];

const IMAGE_EXTENSIONS = [
  ".jpg",
  ".jpeg",
  ".png",
  ".webp",
  ".tiff",
  ".pdf",
] as const;
const IMAGE_MIME_TYPES: readonly string[] = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/tiff",
  "application/pdf",
];

/**
 * MuseScore's own project format, recognised here only so that it can be
 * refused by name.
 *
 * A `.mscz` is a zip carrying the same `META-INF/container.xml` as a `.mxl`,
 * so nothing short of opening it tells the two apart — and the analyzer, which
 * does open it, answers the same thing. Naming the file at drop time means the
 * user gets the way out (an export they already have in their editor) instead
 * of a round trip that ends in "unsupported file".
 */
const MUSESCORE_EXTENSIONS = [".mscz", ".mscx"] as const;

/**
 * Shown when someone drops a MuseScore project. It names the export rather
 * than the failure: that is the only thing they can act on, and it is two
 * clicks away.
 */
export const MUSESCORE_EXPORT_MESSAGE =
  "Ce fichier est un projet MuseScore. Dans MuseScore : Fichier > Exporter > MusicXML, puis redépose le fichier obtenu.";

/**
 * Value of the `accept` attribute on every file input of the upload flow.
 * Single source of truth so a newly supported extension is advertised
 * everywhere at once.
 *
 * It is wider than what Virtuosa can read, by exactly the formats that have a
 * message worth showing. A `.mscz` left out of the list is greyed out by the
 * file picker, which refuses it without saying anything — the one outcome this
 * whole path exists to avoid. What we claim to *read* is
 * `SUPPORTED_EXTENSIONS_LABEL`, and the two lists are deliberately not the
 * same one.
 */
export const UPLOAD_ACCEPT = [
  ...MIDI_EXTENSIONS,
  ...MUSICXML_EXTENSIONS,
  ...IMAGE_EXTENSIONS,
  ...MUSESCORE_EXTENSIONS,
].join(",");

/** The extensions we announce being able to read, as the drop zone spells them out. */
export const SUPPORTED_EXTENSIONS_LABEL = [
  ...MIDI_EXTENSIONS,
  ...MUSICXML_EXTENSIONS,
  ...IMAGE_EXTENSIONS,
].join(", ");

function hasExtension(file: File, extensions: readonly string[]): boolean {
  const name = file.name.toLowerCase();

  return extensions.some((ext) => name.endsWith(ext));
}

/**
 * Family of a file that encodes notes, or `null` when it is not one of those.
 * Extension first — it is what the user chose and what the analyzer keys on;
 * the MIME type only rescues files a browser typed but did not name.
 */
export function detectScoreFormat(file: File): ScoreFormat | null {
  if (hasExtension(file, MIDI_EXTENSIONS) || MIDI_MIME_TYPES.includes(file.type)) {
    return "midi";
  }

  if (
    hasExtension(file, MUSICXML_EXTENSIONS) ||
    MUSICXML_MIME_TYPES.includes(file.type)
  ) {
    return "musicxml";
  }

  return null;
}

/**
 * True for a MuseScore project. Extension only: the front has no business
 * unzipping an archive to find out, and the server checks the bytes anyway.
 */
export function isMuseScoreFile(file: File): boolean {
  return hasExtension(file, MUSESCORE_EXTENSIONS);
}

/** True for a picture of a score: photo, scan or PDF. */
export function isImageFile(file: File): boolean {
  return IMAGE_MIME_TYPES.includes(file.type) || hasExtension(file, IMAGE_EXTENSIONS);
}

/**
 * Family of any accepted file, or `null` when Virtuosa cannot read it. The
 * score formats win over the image test, so a `.pdf` stays an image while a
 * `.musicxml` never falls through to it.
 */
export function classifyUploadFile(file: File): UploadFileKind | null {
  const format = detectScoreFormat(file);

  if (format !== null) {
    return format;
  }

  if (isImageFile(file)) {
    return "image";
  }

  return null;
}

/**
 * Turns a filename into the title we pre-fill: extension dropped, separators
 * turned back into spaces. A plain fallback, overwritten as soon as the
 * inspect step comes back with a real suggestion.
 */
export function humanizeFilename(filename: string): string {
  return filename
    .replace(/\.(musicxml|jpeg|webp|tiff|midi|mid|mxl|xml|jpg|png|pdf)$/i, "")
    .replace(/[-_]/g, " ");
}
